import * as React from 'react'
import { Check, Minus } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * Checkbox — native `<input type="checkbox">` styled to the Figma DS2026
 * "Checkbox" spec (First Bite Design System, node 1517:34684). Native for free
 * accessibility (label association, keyboard) — no extra dependency.
 *
 *   size    sm | md          (16px / 20px box)
 *   status  default | error  (error = red outline + red glyph, per Figma)
 *   indeterminate            (renders the "–" dash; set via the DOM property)
 *
 * Checked = basil-700 fill + white glyph; brand 4px focus ring; disabled =
 * gray-100 fill + gray-300 glyph. Pass `label` for an inline, clickable label,
 * or omit it and wire your own `<FieldLabel htmlFor>`.
 */

type CheckboxSize = 'sm' | 'md'
type CheckboxStatus = 'default' | 'error'

const boxSize: Record<CheckboxSize, string> = {
  sm: 'size-4 rounded',
  md: 'size-5 rounded-md',
}

const glyphSize: Record<CheckboxSize, string> = {
  sm: 'size-3',
  md: 'size-3.5',
}

const statusBox: Record<CheckboxStatus, string> = {
  default: cn(
    '!border-fbui-gray-300 enabled:hover:!border-fbui-gray-400',
    'checked:!border-fbui-basil-700 checked:bg-fbui-basil-700 enabled:checked:hover:!border-fbui-basil-800 enabled:checked:hover:bg-fbui-basil-800',
    'indeterminate:!border-fbui-basil-700 indeterminate:bg-fbui-basil-700 enabled:indeterminate:hover:!border-fbui-basil-800 enabled:indeterminate:hover:bg-fbui-basil-800',
    'focus-visible:ring-fbui-ring-brand'
  ),
  // Error stays white-filled with a red glyph (Figma), so no checked:bg.
  error:
    '!border-fbui-red-500 enabled:hover:!border-fbui-red-600 checked:!border-fbui-red-500 indeterminate:!border-fbui-red-500 focus-visible:ring-fbui-ring-error',
}

const glyphColor: Record<CheckboxStatus, string> = {
  default: 'text-fbui-base-white',
  error: 'text-fbui-red-600',
}

export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'size' | 'type'
> {
  size?: CheckboxSize
  status?: CheckboxStatus
  /** Render the indeterminate "–" state (the DOM property, set via ref). */
  indeterminate?: boolean
  /** Optional inline label, rendered in a clickable `<label>` wrapper. */
  label?: React.ReactNode
  /** Classes on the `<label>`/wrapper (width, alignment). */
  wrapperClassName?: string
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      wrapperClassName,
      size = 'md',
      status = 'default',
      indeterminate = false,
      label,
      disabled,
      ...props
    },
    forwardedRef
  ) => {
    const setRef = React.useCallback(
      (el: HTMLInputElement | null) => {
        if (el) el.indeterminate = indeterminate
        if (typeof forwardedRef === 'function') forwardedRef(el)
        else if (forwardedRef) forwardedRef.current = el
      },
      [forwardedRef, indeterminate]
    )

    const control = (
      <span className="relative inline-flex shrink-0">
        <input
          ref={setRef}
          type="checkbox"
          disabled={disabled}
          className={cn(
            'peer shrink-0 appearance-none border bg-fbui-base-white outline-none transition focus-visible:ring-2',
            'disabled:cursor-not-allowed disabled:!border-fbui-gray-300 disabled:bg-fbui-gray-100',
            'disabled:checked:!border-fbui-gray-300 disabled:checked:bg-fbui-gray-100',
            'disabled:indeterminate:!border-fbui-gray-300 disabled:indeterminate:bg-fbui-gray-100',
            boxSize[size],
            statusBox[status],
            className
          )}
          {...props}
        />
        <Check
          weight="bold"
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 m-auto [opacity:0] peer-checked:[opacity:1] peer-indeterminate:[opacity:0] peer-disabled:text-fbui-text-disabled',
            glyphColor[status],
            glyphSize[size]
          )}
        />
        <Minus
          weight="bold"
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 m-auto [opacity:0] peer-indeterminate:[opacity:1] peer-disabled:text-fbui-text-disabled',
            glyphColor[status],
            glyphSize[size]
          )}
        />
      </span>
    )

    if (!label) {
      return wrapperClassName ? (
        <span className={cn('inline-flex', wrapperClassName)}>{control}</span>
      ) : (
        control
      )
    }

    return (
      <label
        className={cn(
          'inline-flex items-center gap-2.5 tracking-[-0.2px] select-none',
          size === 'sm' ? 'text-xs' : 'text-sm',
          disabled
            ? 'cursor-not-allowed text-fbui-text-disabled'
            : 'cursor-pointer text-fbui-gray-700',
          wrapperClassName
        )}
      >
        {control}
        <span>{label}</span>
      </label>
    )
  }
)
Checkbox.displayName = 'Checkbox'
